// Microservice identifiers
export type Microservice =
  | "core"
  | "payments"
  | "marketing"
  | "notifications"
  | "ssg"
  | "payroll"
  | "onboarding"
  | "voice_ai"
  | "compliance"
  | "reports"
  | "messaging";

// Microservice base URLs from environment variables
export const MICROSERVICE_URLS: Record<Microservice, string> = {
  core: process.env.REACT_APP_CORE_API_URL || "",
  payments: process.env.REACT_APP_PAYMENTS_API_URL || "",
  marketing: process.env.REACT_APP_MARKETING_API_URL || "",
  notifications: process.env.REACT_APP_NOTIFICATIONS_API_URL || "",
  ssg: process.env.REACT_APP_SSG_API_URL || "",
  payroll: process.env.REACT_APP_PAYROLL_API_URL || "",
  onboarding: process.env.REACT_APP_ONBOARDING_API_URL || "",
  voice_ai: process.env.REACT_APP_VOICE_AI_API_URL || "",
  compliance: process.env.REACT_APP_COMPLIANCE_API_URL || "",
  reports: process.env.REACT_APP_REPORTS_API_URL || "",
  messaging: process.env.REACT_APP_MESSAGING_API_URL || "",
};

// Endpoint definition with microservice mapping
interface EndpointConfig {
  path: string;
  service: Microservice;
}

// All API endpoints organized by microservice
// Paths verified against SAM templates in ENDPOINTS.md
export const API_CONFIG = {
  ENDPOINTS: {
    // ==========================================
    // CORE MICROSERVICE - Feature Access
    // ==========================================
    ACCESS: { path: "/access", service: "core" as Microservice },

    // ==========================================
    // CORE MICROSERVICE - Staff Management
    // ==========================================
    STAFF: { path: "/staff", service: "core" as Microservice },
    STAFF_BY_ID: {
      path: "/staff/${staffId}",
      service: "core" as Microservice,
    },
    STAFF_PHONE_EMAIL: {
      path: "/staff/phone-email",
      service: "core" as Microservice,
    },
    SALON_STAFF: {
      path: "/business-locations/${salonId}/staff",
      service: "core" as Microservice,
    },
    // Drag-reorder staff display order for a salon (Staff Settings "Edit Order").
    SALON_STAFF_ORDER: {
      path: "/business-locations/${salonId}/staff/order",
      service: "core" as Microservice,
    },
    INVITE_STAFF: { path: "/staff/invite", service: "core" as Microservice },

    // Staff ad-hoc working-hours overrides ("Schedule Adjustments").
    // Upsert by (staff, date). See types/scheduleAdjustment.ts for the contract.
    STAFF_HOURS_OVERRIDES: {
      path: "/staff/hours-overrides",
      service: "core" as Microservice,
    },
    STAFF_HOURS_OVERRIDE_BY_ID: {
      path: "/staff/hours-overrides/${overrideId}",
      service: "core" as Microservice,
    },
    STAFF_HOURS_OVERRIDES_BY_STAFF: {
      path: "/staff/${staffId}/hours-overrides",
      service: "core" as Microservice,
    },
    STAFF_HOURS_OVERRIDES_EFFECTIVE: {
      path: "/staff/${staffId}/hours-overrides/effective",
      service: "core" as Microservice,
    },
    // Salon-scoped: all staff's overrides in a date range (one query). Used by
    // the calendar day-view shading to overlay ad-hoc hours.
    SALON_HOURS_OVERRIDES: {
      path: "/business_locations/${businessLocationId}/hours-overrides",
      service: "core" as Microservice,
    },
    // Multi-location (PR A / #399): persist which location a multi-location
    // staff member is viewing (staff_users.last_used_salon_id).
    STAFF_USERS_ME: {
      path: "/staff-users/me",
      service: "core" as Microservice,
    },
    CONFIRM_EMAIL: {
      path: "/confirm-email",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Commissions
    // ==========================================
    STAFF_COMMISSIONS: {
      path: "/staff/${staffId}/commissions",
      service: "core" as Microservice,
    },
    COMMISSIONS: {
      path: "/commissions",
      service: "core" as Microservice,
    },
    COMMISSION_BY_ID: {
      path: "/commissions/${commissionId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Staff Tiers
    // ==========================================
    STAFF_TIERS: {
      path: "/business-locations/${salonId}/staff-tiers",
      service: "core" as Microservice,
    },
    STAFF_TIERS_ORDER: {
      path: "/business-locations/${salonId}/staff-tiers/order",
      service: "core" as Microservice,
    },
    STAFF_TIER_BY_ID: {
      path: "/staff-tiers/${tierId}",
      service: "core" as Microservice,
    },
    STAFF_TIER_ASSIGN: {
      path: "/business-locations/${salonId}/staff/${staffId}/tier",
      service: "core" as Microservice,
    },
    STAFF_TIER_COMMISSION_DEFAULTS: {
      path: "/staff-tiers/${tierId}/commission-defaults",
      service: "core" as Microservice,
    },
    STAFF_TIER_APPLY_COMMISSION_DEFAULTS: {
      path: "/staff-tiers/${tierId}/apply-commission-defaults",
      service: "core" as Microservice,
    },
    STAFF_TIER_SERVICE_DEFAULTS: {
      path: "/staff-tiers/${tierId}/service-defaults",
      service: "core" as Microservice,
    },
    STAFF_TIER_SERVICE_DEFAULT_BY_SERVICE: {
      path: "/staff-tiers/${tierId}/service-defaults/${salonServiceId}",
      service: "core" as Microservice,
    },
    STAFF_TIER_APPLY_SERVICE_DEFAULTS: {
      path: "/staff-tiers/${tierId}/apply-service-defaults",
      service: "core" as Microservice,
    },
    STAFF_TIER_REASSIGN_AND_DELETE: {
      path: "/staff-tiers/${tierId}/reassign-and-delete",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Salon Management
    // ==========================================
    SALONS: { path: "/business-locations", service: "core" as Microservice },
    SALON_BY_ID: {
      path: "/business-locations/${salonId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Services
    // ==========================================
    SERVICES: { path: "/services", service: "core" as Microservice },
    SERVICES_BY_SALON: {
      path: "/business-locations/${salonId}/services",
      service: "core" as Microservice,
    },
    SALON_SERVICES: {
      path: "/salon-services/${salonServiceId}",
      service: "core" as Microservice,
    },
    STAFF_SERVICES: {
      path: "/staff-services/${staffServiceId}",
      service: "core" as Microservice,
    },
    // Bulk-set the business charge (house cut) on many services/products at once.
    SET_BUSINESS_CHARGES: {
      path: "/business-locations/${businessLocationId}/business-charges",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Products
    // ==========================================
    PRODUCTS: { path: "/products", service: "core" as Microservice },
    SALON_PRODUCTS: {
      path: "/business-locations/${salonId}/products",
      service: "core" as Microservice,
    },
    PRODUCT_STOCK_ADJUSTMENTS: {
      path: "/products/${productId}/stock-adjustments",
      service: "core" as Microservice,
    },
    PRODUCT_STOCK_ADJUSTMENT_BY_ID: {
      path: "/product-stock-adjustments/${adjustmentId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Policies
    // ==========================================
    POLICIES: { path: "/policies", service: "core" as Microservice },
    SALON_POLICIES: {
      path: "/business-locations/${salonId}/policies",
      service: "core" as Microservice,
    },
    POLICY_BY_ID: {
      path: "/policies/${policyId}",
      service: "core" as Microservice,
    },
    POLICIES_DEFAULTS: {
      path: "/policies/defaults",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Deposit Configs
    // ==========================================
    DEPOSIT_CONFIGS: {
      path: "/business-locations/${salonId}/deposit-configs",
      service: "core" as Microservice,
    },
    DEPOSIT_CONFIG_BY_ID: {
      path: "/business-locations/${salonId}/deposit-configs/${depositConfigId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Off-Platform Payment Methods
    // ==========================================
    SALON_PAYMENT_METHODS: {
      path: "/business-locations/${salonId}/payment-methods",
      service: "core" as Microservice,
    },
    SALON_PAYMENT_METHOD_BY_ID: {
      path: "/business-locations/${salonId}/payment-methods/${paymentMethodId}",
      service: "core" as Microservice,
    },
    SALON_PAYMENT_METHODS_SELECTION: {
      path: "/business-locations/${salonId}/payment-methods/selection",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Discount Presets (per-location catalog)
    // ==========================================
    SALON_DISCOUNT_PRESETS: {
      path: "/business-locations/${salonId}/discount-presets",
      service: "core" as Microservice,
    },
    DISCOUNT_PRESET_BY_ID: {
      path: "/business-locations/${salonId}/discount-presets/${presetId}",
      service: "core" as Microservice,
    },
    DISCOUNT_PRESETS_QUICK_DISCOUNTS: {
      path: "/business-locations/${businessLocationId}/discount-presets/quick-discounts",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Service Adjustment Forms
    // ==========================================
    SERVICE_ADJUSTMENT_FORMS: {
      path: "/business-locations/${salonId}/service-adjustment-forms",
      service: "core" as Microservice,
    },
    SERVICE_ADJUSTMENT_FORM_BY_ID: {
      path: "/business-locations/${salonId}/service-adjustment-forms/${formId}",
      service: "core" as Microservice,
    },
    SERVICE_ADJUSTMENT_FORM_JUNCTIONS: {
      path: "/business-locations/${salonId}/service-adjustment-form-junctions",
      service: "core" as Microservice,
    },
    SERVICE_ADJUSTMENT_FORM_JUNCTION_BY_ID: {
      path: "/business-locations/${salonId}/service-adjustment-form-junctions/${junctionId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // COMPLIANCE MICROSERVICE - Form Templates (Dynamic Forms & Waivers)
    // ==========================================
    FORM_TEMPLATES: {
      path: "/business-locations/${salonId}/form-templates",
      service: "compliance" as Microservice,
    },
    FORM_TEMPLATE_BY_ID: {
      path: "/business-locations/${salonId}/form-templates/${templateId}",
      service: "compliance" as Microservice,
    },
    // Phase 2 (not yet shipped) — referenced only by FormViewer stubs that
    // console.log the Phase-2-shaped payload so the UI is drop-in ready.
    CLIENT_FORMS: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_BY_ID: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_SUBMIT: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/submit",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_EXPIRE: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/expire",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_MARK_OPENED: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/mark-opened",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_RENAME: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/rename",
      service: "compliance" as Microservice,
    },
    CLIENT_FORM_UPLOADS: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/uploads",
      service: "compliance" as Microservice,
    },
    /** POST batch: resolve phi/ s3_keys to presigned GET URLs for display. */
    CLIENT_FORM_UPLOADS_READ: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/uploads/read",
      service: "compliance" as Microservice,
    },
    /** POST: soft-delete one image_upload entry — tombstones the
     * salon_documents row AND patches the form's data_json pointer. */
    CLIENT_FORM_IMAGE_SOFT_DELETE: {
      path: "/business-locations/${salonId}/clients/${clientId}/forms/${formId}/fields/${fieldId}/uploads/soft-delete",
      service: "compliance" as Microservice,
    },
    SEND_FORM_LINK: {
      path: "/appointments/${transactionUuid}/forms/send-manual",
      service: "compliance" as Microservice,
    },
    SERVICE_REQUIRED_FORMS: {
      path: "/business-locations/${salonId}/services/${salonServiceId}/required-forms",
      service: "compliance" as Microservice,
    },
    SERVICE_REQUIRED_FORM_BY_ID: {
      path: "/business-locations/${salonId}/services/${salonServiceId}/required-forms/${requirementId}",
      service: "compliance" as Microservice,
    },
    SERVICE_PRE_APPOINTMENT_DOCUMENTS: {
      path: "/business-locations/${salonId}/services/${salonServiceId}/pre-appointment-documents",
      service: "compliance" as Microservice,
    },
    SERVICE_PRE_APPOINTMENT_DOCUMENT_BY_ID: {
      path: "/business-locations/${salonId}/services/${salonServiceId}/pre-appointment-documents/${attachmentUuid}",
      service: "compliance" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Message Templates (med_spa)
    // ==========================================
    MESSAGE_TEMPLATES: {
      path: "/message-templates",
      service: "core" as Microservice,
    },
    MESSAGE_TEMPLATE_BY_ID: {
      path: "/message-templates/${templateUuid}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Per-Service Message Settings (all verticals)
    // ==========================================
    // GET + PUT the three on/off message toggles for a salon service.
    SERVICE_MESSAGE_SETTINGS: {
      path: "/salon-services/${salonServiceUuid}/message-settings",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Per-Service Post-Appointment Messages
    // ==========================================
    SERVICE_POST_APPOINTMENT_SETTINGS: {
      path: "/salon-services/${salonServiceId}/post-appointment-settings",
      service: "core" as Microservice,
    },
    SERVICE_POST_APPOINTMENT_MESSAGES: {
      path: "/salon-services/${salonServiceId}/post-appointment-messages",
      service: "core" as Microservice,
    },
    SERVICE_POST_APPOINTMENT_MESSAGE_BY_ID: {
      path: "/salon-services/${salonServiceId}/post-appointment-messages/${messageUuid}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Time Off
    // ==========================================
    TIME_OFF: { path: "/timeoff", service: "core" as Microservice },
    TIME_OFF_BY_SALON: {
      path: "/business-locations/${salonId}/timeoff",
      service: "core" as Microservice,
    },
    TIME_OFF_BY_STAFF: {
      path: "/staff/${staffId}/timeoff",
      service: "core" as Microservice,
    },
    TIME_OFF_DELETE_SINGLE: {
      path: "/timeoff/${timeOffId}/single",
      service: "core" as Microservice,
    },
    TIME_OFF_DELETE_HEREAFTER: {
      path: "/timeoff/${timeOffId}/hereafter",
      service: "core" as Microservice,
    },
    TIME_OFF_DELETE_ALL: {
      path: "/timeoff/${timeOffId}/all",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Clients
    // ==========================================
    CLIENTS: { path: "/clients", service: "core" as Microservice },
    CLIENTS_BY_ID: {
      path: "/clients/${clientId}",
      service: "core" as Microservice,
    },
    SALON_CLIENTS: {
      path: "/business-locations/${salonId}/clients",
      service: "core" as Microservice,
    },
    MERGE_CLIENTS: {
      path: "/business-locations/${salonId}/clients/merge",
      service: "core" as Microservice,
    },
    // Client Notes & Formulations
    CLIENT_NOTE: {
      path: "/clients/${clientId}/note",
      service: "core" as Microservice,
    },
    CLIENT_NOTE_BY_ID: {
      path: "/clients/${clientId}/note/${noteId}",
      service: "core" as Microservice,
    },
    CLIENT_FORMULATION: {
      path: "/clients/${clientId}/formulation",
      service: "core" as Microservice,
    },
    CLIENT_FORMULATION_BY_ID: {
      path: "/clients/${clientId}/formulation/${formulationId}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Service Packages (prepaid bundles)
    // ==========================================
    SERVICE_PACKAGES: {
      path: "/business-locations/${salonId}/service-packages",
      service: "core" as Microservice,
    },
    SERVICE_PACKAGE_BY_ID: {
      path: "/business-locations/${salonId}/service-packages/${packageId}",
      service: "core" as Microservice,
    },
    CLIENT_SERVICE_PACKAGES: {
      path: "/business-locations/${salonId}/clients/${clientId}/service-packages",
      service: "core" as Microservice,
    },
    // Dedicated, keyset-paginated Activity Timeline feed (packages + gift cards
    // + claims + appointments + quick sales). Backs the client-detail Activity
    // tab; eager-prefetched on ClientDetails mount.
    CLIENT_ACTIVITY: {
      path: "/business-locations/${salonId}/clients/${clientId}/activity",
      service: "core" as Microservice,
    },
    SELL_SERVICE_PACKAGE: {
      path: "/business-locations/${salonId}/service-packages/sell",
      service: "core" as Microservice,
    },
    REDEEM_SERVICE_PACKAGE: {
      path: "/business-locations/${salonId}/redeem-package",
      service: "core" as Microservice,
    },
    CLIENT_PACKAGE_ADJUSTMENTS: {
      path: "/business-locations/${salonId}/client-service-packages/${cspId}/adjustments",
      service: "core" as Microservice,
    },
    // Manual package management — grant reuses CLIENT_SERVICE_PACKAGES (POST to
    // the same collection path). Adjust/expiry/revoke act on an owned package.
    ADJUST_CLIENT_PACKAGE_BALANCE: {
      path: "/business-locations/${salonId}/client-service-packages/${cspId}/balances/${salonServiceId}/adjust",
      service: "core" as Microservice,
    },
    EDIT_CLIENT_PACKAGE_EXPIRY: {
      path: "/business-locations/${salonId}/client-service-packages/${cspId}/expiry",
      service: "core" as Microservice,
    },
    REVOKE_CLIENT_PACKAGE: {
      path: "/business-locations/${salonId}/client-service-packages/${cspId}/revoke",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Gift Cards (prepaid stored-value balances)
    // ==========================================
    // Salon-scoped like the deposit-config + package keys (the gateway maps
    // salon → business_location). GET/PUT the salon config; GET the client's
    // held/purchased cards; POST to the collection path to manually grant.
    GIFT_CARD_CONFIG: {
      path: "/business-locations/${salonId}/gift-card-config",
      service: "core" as Microservice,
    },
    CLIENT_GIFT_CARDS: {
      path: "/business-locations/${salonId}/clients/${clientId}/gift-cards",
      service: "core" as Microservice,
    },
    ADJUST_GIFT_CARD: {
      path: "/business-locations/${salonId}/gift-cards/${giftCardId}/adjust",
      service: "core" as Microservice,
    },
    VOID_GIFT_CARD: {
      path: "/business-locations/${salonId}/gift-cards/${giftCardId}/void",
      service: "core" as Microservice,
    },
    UPDATE_GIFT_CARD_CODE: {
      path: "/business-locations/${salonId}/gift-cards/${giftCardId}/code",
      service: "core" as Microservice,
    },
    GIFT_CARD_LEDGER: {
      path: "/business-locations/${salonId}/gift-cards/${giftCardId}/ledger",
      service: "core" as Microservice,
    },
    // Phase 2 — staff-sold lifecycle (sell → notify/attach → redeem). These
    // live on the core SENARY router, which never had a legacy salons-prefixed
    // twin. Pass the salon UUID (businessInfo.id) as businessLocationId — the
    // same value the gift-card keys above use as salonId.
    SELL_GIFT_CARD: {
      path: "/business-locations/${businessLocationId}/gift-cards/sell",
      service: "core" as Microservice,
    },
    TRANSACTION_GIFT_CARDS: {
      path: "/business-locations/${businessLocationId}/transactions/${transactionId}/gift-cards",
      service: "core" as Microservice,
    },
    REDEEM_GIFT_CARD: {
      path: "/business-locations/${businessLocationId}/gift-cards/redeem",
      service: "core" as Microservice,
    },
    LOOKUP_GIFT_CARD_BY_CODE: {
      path: "/business-locations/${businessLocationId}/gift-cards/lookup-by-code",
      service: "core" as Microservice,
    },
    ATTACH_GIFT_CARD: {
      path: "/business-locations/${businessLocationId}/gift-cards/${giftCardId}/attach",
      service: "core" as Microservice,
    },
    SEND_GIFT_CARD_LINK: {
      path: "/business-locations/${businessLocationId}/gift-cards/${giftCardId}/send-link",
      service: "core" as Microservice,
    },
    // Phase 3 — is a staff-typed custom code free in this location? (as-you-type
    // hint behind the sell + add-line-item code fields). Boolean only.
    CHECK_GIFT_CARD_CODE: {
      path: "/business-locations/${businessLocationId}/gift-cards/code-available",
      service: "core" as Microservice,
    },
    // Sales → Gift Cards tab: salon-wide list of every gift card at the location
    // → { gift_cards: [...] }. Pass businessInfo.id as businessLocationId (same
    // value the /business-locations gift-card keys use as salonId).
    LIST_GIFT_CARDS: {
      path: "/business-locations/${businessLocationId}/gift-cards",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Appointments & Transactions
    // ==========================================
    APPOINTMENTS: { path: "/appointments", service: "core" as Microservice },
    TRANSACTIONS: { path: "/transactions", service: "core" as Microservice },
    TRANSACTIONS_BY_SALON: {
      path: "/business-locations/${salonId}/transactions",
      service: "core" as Microservice,
    },
    // Sales tab (BE-1): GET /business-locations/{id}/sales?view=transactions|purchases
    // Same base shape as TRANSACTIONS_BY_SALON PLUS refunds[], per-payment
    // refunded_amount, and is_future_deposit_only. Gated on payments.canView.
    SALES_BY_BUSINESS_LOCATION: {
      // Deployed API Gateway route is hyphenated (/business-locations/...); the
      // router normalizes it to the internal underscore form. Using the
      // underscore path here 403s at the gateway before reaching the Lambda.
      path: "/business-locations/${businessLocationId}/sales",
      service: "core" as Microservice,
    },
    // Sales tab void: POST {preview: true} → verdict only; POST {} → void.
    // Hyphenated route for the same gateway reason as the sales route above.
    VOID_TRANSACTION: {
      path: "/business-locations/${businessLocationId}/transactions/${transactionId}/void",
      service: "core" as Microservice,
    },
    TRANSACTION_LINE_ITEMS: {
      path: "/transactions/line-items",
      service: "core" as Microservice,
    },
    LINE_ITEM_DELETE: {
      path: "/line-items/${lineItemUuid}",
      service: "core" as Microservice,
    },
    ADD_LINE_ITEM_TO_TRANSACTION: {
      path: "/add-line-item-to-transaction",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Availability
    // ==========================================
    AVAILABILITY: { path: "/availability", service: "core" as Microservice },

    // Booking-flow redesign: server-computed suggested appointment start times.
    // GET /suggested-times?services=<comma>&date=<YYYY-MM-DD>&timezone=<IANA>[&staff_ids=<comma, aligned>][&start_time=HH:mm]
    SUGGESTED_TIMES: {
      path: "/suggested-times",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Non-Card Payments
    // ==========================================
    PROCESS_NON_CARD_PAYMENT: {
      path: "/process-non-card-payment",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Cash Drawer
    // ==========================================
    CASH_DRAWER_SESSIONS: {
      path: "/business-locations/${salonId}/cash-drawer-sessions",
      service: "core" as Microservice,
    },
    CASH_DRAWER_SESSION_COUNT: {
      path: "/cash-drawer-sessions/${sessionId}/count",
      service: "core" as Microservice,
    },
    CASH_DRAWER_SESSION_TRANSACTIONS: {
      path: "/cash-drawer-sessions/${sessionId}/transactions",
      service: "core" as Microservice,
    },
    CASH_TRANSACTION_VOID: {
      path: "/cash-transactions/${transactionId}/void",
      service: "core" as Microservice,
    },
    CASH_DRAWER_SESSION_BALANCING_DATE: {
      path: "/cash-drawer-sessions/${sessionId}/balancing-date",
      service: "core" as Microservice,
    },
    CASH_DRAWER_BALANCING_SUMMARY: {
      path: "/cash-drawer-sessions/${sessionId}/balancing-summary",
      service: "reports" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Subscriptions
    // ==========================================
    GET_SUBSCRIBE_URL: {
      path: "/get-subscribe-url",
      service: "payments" as Microservice,
    },
    GET_SUBSCRIPTION_PRICING: {
      path: "/subscription-pricing/${tier}",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Stripe Connect
    // ==========================================
    CREATE_STRIPE_CONNECT_ACCOUNT: {
      path: "/create-stripe-connect-account",
      service: "payments" as Microservice,
    },
    GET_STRIPE_CONNECT_ACCOUNT_LINK: {
      path: "/get-stripe-connect-account-link",
      service: "payments" as Microservice,
    },
    INITIALIZE_STRIPE_CONNECT_ACCOUNT_SESSION: {
      path: "/create-stripe-connect-account-session",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Terminal Devices
    // ==========================================
    REGISTER_STRIPE_TERMINAL_DEVICE: {
      path: "/register-stripe-terminal-device",
      service: "payments" as Microservice,
    },
    GET_STRIPE_TERMINAL_DEVICE: {
      path: "/get-stripe-terminal-device",
      service: "payments" as Microservice,
    },
    REMOVE_STRIPE_TERMINAL_DEVICE: {
      path: "/remove-stripe-terminal-device",
      service: "payments" as Microservice,
    },
    SET_TERMINAL_CONFIGURATION: {
      path: "/set-terminal-configuration",
      service: "payments" as Microservice,
    },
    PAYMENT_TERMINALS: {
      path: "/payment-terminals",
      service: "payments" as Microservice,
    },
    PAYMENT_TERMINAL_BY_ID: {
      path: "/payment-terminals/${terminalId}",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Payment Intents
    // ==========================================
    CREATE_PAYMENT_INTENT: {
      path: "/create-payment-intent",
      service: "payments" as Microservice,
    },
    UPDATE_PAYMENT_INTENT: {
      path: "/update-payment-intent",
      service: "payments" as Microservice,
    },
    CANCEL_PAYMENT_INTENT: {
      path: "/cancel-payment-intent",
      service: "payments" as Microservice,
    },
    PROCESS_PAYMENT: {
      path: "/process-payment",
      service: "payments" as Microservice,
    },
    SIMULATE_PAYMENT: {
      path: "/simulate-payment",
      service: "payments" as Microservice,
    },
    POLL_STRIPE_PAYMENT_STATUS: {
      path: "/poll-stripe-payment-status",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Quick Sales
    // ==========================================
    CREATE_QUICK_SALE: {
      path: "/create-quick-sale",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Deposits
    // ==========================================
    GENERATE_DEPOSIT_LINK: {
      path: "/generate-deposit-link",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Card on File
    // ==========================================
    CREATE_SETUP_INTENT: {
      path: "/clients/${clientId}/setup-intent",
      service: "payments" as Microservice,
    },
    CLIENT_PAYMENT_METHODS: {
      path: "/clients/${clientId}/payment-methods",
      service: "payments" as Microservice,
    },
    CLIENT_PAYMENT_METHOD_BY_ID: {
      path: "/clients/${clientId}/payment-methods/${pmId}",
      service: "payments" as Microservice,
    },
    CLIENT_PAYMENT_METHOD_DEFAULT: {
      path: "/clients/${clientId}/payment-methods/${pmId}/default",
      service: "payments" as Microservice,
    },
    CLIENT_PAYMENT_METHOD_NICKNAME: {
      path: "/clients/${clientId}/payment-methods/${pmId}/nickname",
      service: "payments" as Microservice,
    },
    CHARGE_CARD_ON_FILE: {
      path: "/charge-card-on-file",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Refunds
    // ==========================================
    REFUND_PAYMENT: {
      path: "/refund-payment",
      service: "payments" as Microservice,
    },

    // ==========================================
    // PAYMENTS MICROSERVICE - Receipts
    // ==========================================
    // Emails a Stripe receipt for a card-backed payment. Body { email? }:
    // when omitted the backend uses the payment's linked email.
    SEND_RECEIPT: {
      path: "/payments/${paymentId}/send-receipt",
      service: "payments" as Microservice,
    },

    // ==========================================
    // MARKETING MICROSERVICE - Templates & Values
    // ==========================================
    MARKETING_TEMPLATE_VARIABLES: {
      path: "/marketing/templated-values/${salonId}",
      service: "marketing" as Microservice,
    },
    MARKETING_TEMPLATES: {
      path: "/marketing/templates",
      service: "marketing" as Microservice,
    },

    // ==========================================
    // MARKETING MICROSERVICE - Compliance
    // ==========================================
    MARKETING_COMPLIANCE_MESSAGES: {
      path: "/marketing/compliance-messages",
      service: "marketing" as Microservice,
    },
    MARKETING_CHECK_COMPLIANCE: {
      path: "/marketing/check-compliance",
      service: "marketing" as Microservice,
    },

    // ==========================================
    // MARKETING MICROSERVICE - Campaigns
    // ==========================================
    MARKETING_CAMPAIGNS: {
      path: "/marketing/campaigns",
      service: "marketing" as Microservice,
    },
    SALON_CAMPAIGNS: {
      path: "/business-locations/${salonId}/campaigns",
      service: "marketing" as Microservice,
    },
    MARKETING_CAMPAIGN_BY_ID: {
      path: "/marketing/campaigns/${campaignId}",
      service: "marketing" as Microservice,
    },

    // ==========================================
    // NOTIFICATIONS MICROSERVICE
    // ==========================================
    PUSHER_AUTH: {
      path: "/pusher/auth",
      service: "notifications" as Microservice,
    },
    NOTIFICATIONS: {
      path: "/notifications",
      service: "notifications" as Microservice,
    },
    NOTIFICATION_PREFERENCES: {
      path: "/notification-preferences",
      service: "notifications" as Microservice,
    },

    // ==========================================
    // STATIC SITE GENERATOR MICROSERVICE - Preview & Deployment
    // ==========================================
    GENERATE_PREVIEW: {
      path: "/static-web-generator/${salonId}/generate-preview",
      service: "ssg" as Microservice,
    },
    LOAD_PREVIEW_DATA: {
      path: "/business-locations/${salonId}/preview-data",
      service: "ssg" as Microservice,
    },
    POPULATE_SITE_TEMPLATE: {
      path: "/populate-site-template/${salonId}",
      service: "ssg" as Microservice,
    },
    GET_DEPLOY_STATUS: {
      path: "/static-web-generator/${salonId}/deploy-status",
      service: "ssg" as Microservice,
    },
    REBUILD_WEBSITE: {
      path: "/static-web-generator/${salonId}/rebuild",
      service: "ssg" as Microservice,
    },

    // ==========================================
    // STATIC SITE GENERATOR MICROSERVICE - Domain & Subdomain Management
    // ==========================================
    REGISTER_CUSTOM_DOMAIN: {
      path: "/static-web-generator/${salonId}/register-domain",
      service: "ssg" as Microservice,
    },
    LIST_ALL_DOMAINS: {
      path: "/static-web-generator/${salonId}/domains",
      service: "ssg" as Microservice,
    },
    CLAIM_UPDATE_SUBDOMAIN: {
      path: "/static-web-generator/${salonId}/subdomains",
      service: "ssg" as Microservice,
    },
    GET_CURRENT_SUBDOMAIN: {
      path: "/static-web-generator/${salonId}/subdomains/current",
      service: "ssg" as Microservice,
    },
    CHECK_SUBDOMAIN_AVAILABILITY: {
      path: "/static-web-generator/subdomains/check",
      service: "ssg" as Microservice,
    },
    VISIT_SUBDOMAIN: {
      path: "/static-web-generator/subdomain/${salonId}",
      service: "ssg" as Microservice,
    },
    ADD_GOOGLE_ANALYTICS: {
      path: "/static-web-generator/${salonId}/add-google-analytics",
      service: "ssg" as Microservice,
    },

    // ==========================================
    // STATIC SITE GENERATOR MICROSERVICE - Domain Router
    // ==========================================
    GET_DOMAIN_MAPPINGS: {
      path: "/domain-router/mappings",
      service: "ssg" as Microservice,
    },

    // ==========================================
    // STATIC SITE GENERATOR MICROSERVICE - Images
    // ==========================================
    CREATE_IMAGE: {
      path: "/create-image",
      service: "ssg" as Microservice,
    },
    REMOVE_SALON_LOGO: {
      path: "/remove-salon-logo",
      service: "ssg" as Microservice,
    },
    GET_SALON_IMAGES: {
      path: "/images/${salonId}",
      service: "ssg" as Microservice,
    },
    DELETE_IMAGE: {
      path: "/images/${salonId}/${filename}",
      service: "ssg" as Microservice,
    },

    // ==========================================
    // COMPLIANCE MICROSERVICE - Salon Documents
    // (Moved from SSG — SSG copies still serve during cutover window; safe to flip now.)
    // ==========================================
    LIST_SALON_DOCUMENTS: {
      path: "/business-locations/${salonId}/documents",
      service: "compliance" as Microservice,
    },
    UPLOAD_SALON_DOCUMENT: {
      path: "/business-locations/${salonId}/documents",
      service: "compliance" as Microservice,
    },
    GET_SALON_DOCUMENT: {
      path: "/business-locations/${salonId}/documents/${documentKey}",
      service: "compliance" as Microservice,
    },
    /** Same path as GET detail; method DELETE removes document and all versions. */
    DELETE_SALON_DOCUMENT: {
      path: "/business-locations/${salonId}/documents/${documentKey}",
      service: "compliance" as Microservice,
    },
    REVERT_SALON_DOCUMENT: {
      path: "/business-locations/${salonId}/documents/${documentKey}/revert",
      service: "compliance" as Microservice,
    },
    DOWNLOAD_SALON_DOCUMENT: {
      path: "/business-locations/${salonId}/documents/${documentKey}/download",
      service: "compliance" as Microservice,
    },

    // ==========================================
    // PAYROLL MICROSERVICE
    // ==========================================
    ROLLFI_PAYROLL_PERIOD: {
      path: "/rollfi/payroll-period",
      service: "payroll" as Microservice,
    },
    ROLLFI_ACTIVE_USERS: {
      path: "/rollfi/active-users",
      service: "payroll" as Microservice,
    },
    ROLLFI_SYNC_PAYROLL: {
      path: "/rollfi/sync-payroll",
      service: "payroll" as Microservice,
    },
    ROLLFI_HOURS_RATE: {
      path: "/rollfi/hours-rate",
      service: "payroll" as Microservice,
    },
    ROLLFI_CREATE_BUSINESS: {
      path: "/rollfi/business",
      service: "payroll" as Microservice,
    },
    ROLLFI_SSO_INITIATE: {
      path: "/sso/initiate",
      service: "payroll" as Microservice,
    },
    TIME_TRACKING_STATUS: {
      path: "/time-tracking/${employeeId}",
      service: "payroll" as Microservice,
    },
    TIME_TRACKING_EMPLOYEE: {
      path: "/time-tracking/employee",
      service: "payroll" as Microservice,
    },
    TIME_TRACKING_ACTION: {
      path: "/time-tracking",
      service: "payroll" as Microservice,
    },
    PREP_PAYROLL_SYNC: {
      path: "/prep-payroll-sync",
      service: "payroll" as Microservice,
    },

    // ==========================================
    // ONBOARDING MICROSERVICE
    // ==========================================
    ONBOARDING_PROGRESS: {
      path: "/onboardings",
      service: "onboarding" as Microservice,
    },
    ONBOARDING_ANSWERS: {
      path: "/onboardings/initial_onboarding/answers",
      service: "onboarding" as Microservice,
    },
    ACCOUNT_RECOVERY: {
      path: "/account-recovery",
      service: "onboarding" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Role Permissions (RBAC)
    // ==========================================
    ROLE_PERMISSIONS: {
      path: "/business-locations/${salonId}/role-permissions",
      service: "core" as Microservice,
    },
    ROLE_PERMISSIONS_RESET: {
      path: "/business-locations/${salonId}/role-permissions/${role}",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Tax Rates (Phase 2 admin UI)
    // ==========================================
    SALON_TAX_RATES: {
      path: "/business-locations/${salonId}/tax-rates",
      service: "core" as Microservice,
    },
    TAX_RATE_BY_ID: {
      path: "/tax-rates/${taxRateId}",
      service: "core" as Microservice,
    },
    PRODUCT_TAX_RATES: {
      path: "/products/${productId}/tax-rates",
      service: "core" as Microservice,
    },
    SALON_SERVICE_TAX_RATES: {
      path: "/business-locations/${salonId}/services/${salonServiceId}/tax-rates",
      service: "core" as Microservice,
    },

    // ==========================================
    // VOICE AI MICROSERVICE
    // ==========================================
    VOICE_AI_SETTINGS: {
      path: "/voice-ai/settings",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_PHONE_NUMBERS: {
      path: "/voice-ai/phone-numbers",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_PHONE_NUMBER_BY_ID: {
      path: "/voice-ai/phone-numbers/${phoneNumberId}",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_AVAILABLE_NUMBERS: {
      path: "/voice-ai/available-numbers",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_CALLS: {
      path: "/voice-ai/calls",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_SCHEDULES: {
      path: "/voice-ai/schedules",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_SCHEDULE_BY_ID: {
      path: "/voice-ai/schedules/${scheduleId}",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_MESSAGES: {
      path: "/voice-ai/messages",
      service: "voice_ai" as Microservice,
    },
    VOICE_AI_MESSAGE_BY_ID: {
      path: "/voice-ai/messages/${messageId}",
      service: "voice_ai" as Microservice,
    },

    // ==========================================
    // MESSAGING MICROSERVICE - Two-way SMS/MMS inbox
    // ==========================================
    // List a location's conversations (GET, ?business_location_uuid=&limit=&offset=).
    MESSAGING_CONVERSATIONS: {
      path: "/messaging/conversations",
      service: "messaging" as Microservice,
    },
    // One conversation's thread (GET ?limit=&before=) + interactive send (POST).
    // ${conversationId} is the opaque, shareable base64url token from the list.
    MESSAGING_CONVERSATION_MESSAGES: {
      path: "/messaging/conversations/${conversationId}/messages",
      service: "messaging" as Microservice,
    },
    // Shared team read/unread state (PUT { is_unread }).
    MESSAGING_CONVERSATION_READ_STATE: {
      path: "/messaging/conversations/${conversationId}/read-state",
      service: "messaging" as Microservice,
    },
    // Link / reassign / detach the conversation's client (PUT { client_uuid|null }).
    MESSAGING_CONVERSATION_CLIENT: {
      path: "/messaging/conversations/${conversationId}/client",
      service: "messaging" as Microservice,
    },
    // Per-conversation scheduled sends: list (GET) + schedule new (POST).
    MESSAGING_CONVERSATION_SCHEDULED: {
      path: "/messaging/conversations/${conversationId}/scheduled",
      service: "messaging" as Microservice,
    },
    // One scheduled message: edit / reschedule (PUT) + cancel (DELETE).
    MESSAGING_SCHEDULED_ITEM: {
      path: "/messaging/scheduled/${scheduledId}",
      service: "messaging" as Microservice,
    },
    // Fire a pending scheduled message immediately (POST).
    MESSAGING_SCHEDULED_SEND_NOW: {
      path: "/messaging/scheduled/${scheduledId}/send-now",
      service: "messaging" as Microservice,
    },
    // Stage an outbound MMS image (POST { business_location_uuid, content_type, data_base64 }).
    MESSAGING_MEDIA: {
      path: "/messaging/media",
      service: "messaging" as Microservice,
    },

    // ==========================================
    // REPORTS MICROSERVICE
    // ==========================================
    REPORT_DEFINITIONS: {
      path: "/report-definitions",
      service: "reports" as Microservice,
    },
    SALON_REPORTS: {
      path: "/business-locations/${salonId}/reports",
      service: "reports" as Microservice,
    },
    SALON_REPORT_BY_ID: {
      path: "/business-locations/${salonId}/reports/${reportId}",
      service: "reports" as Microservice,
    },
    RUN_SALON_REPORT: {
      path: "/business-locations/${salonId}/reports/${reportId}/run",
      service: "reports" as Microservice,
    },
    // Report sharing (reports.canShareReports): GET who has a copy + who
    // else can get one; POST share; PUT one copy's sync; DELETE revoke.
    SALON_REPORT_SHARES: {
      path: "/business-locations/${salonId}/reports/${reportId}/shares",
      service: "reports" as Microservice,
    },
    SALON_REPORT_SHARE_BY_ID: {
      path: "/business-locations/${salonId}/reports/${reportId}/shares/${shareId}",
      service: "reports" as Microservice,
    },
    // New-engine (v2) reports, e.g. the Daily Sales Report. Body: optional
    // { range, filters, groups, granularity } overrides of the saved view.
    RUN_SALON_REPORT_V2: {
      path: "/business-locations/${salonId}/reports/${reportId}/v2/run",
      service: "reports" as Microservice,
    },

    // ==========================================
    // COMPLIANCE MICROSERVICE - Appointment Forms (med-spa, PR 5)
    // Writes PHI to client_forms; lives behind the compliance_core boundary.
    // ==========================================
    APPOINTMENT_FORMS: {
      path: "/appointments/${transactionUuid}/forms",
      service: "compliance" as Microservice,
    },
    APPOINTMENT_FORM_REMOVE: {
      path: "/appointments/${transactionUuid}/forms/${clientFormUuid}",
      service: "compliance" as Microservice,
    },

    // ==========================================
    // COMPLIANCE MICROSERVICE - Appointment Scheduled Messages (med-spa, PR 5)
    // Triggered from PHI-bearing appointment contexts; co-located with the
    // appointment_forms surface on compliance_core.
    // ==========================================
    APPOINTMENT_SCHEDULED_MESSAGES: {
      path: "/appointments/${transactionUuid}/scheduled-messages",
      service: "compliance" as Microservice,
    },
    APPOINTMENT_SCHEDULED_MESSAGE_CANCEL: {
      path: "/appointments/${transactionUuid}/scheduled-messages/${messageUuid}",
      service: "compliance" as Microservice,
    },
    APPOINTMENT_SCHEDULED_MESSAGE_SEND: {
      path: "/appointments/${transactionUuid}/scheduled-messages/${messageUuid}/send",
      service: "compliance" as Microservice,
    },
    APPOINTMENT_SCHEDULED_MESSAGE_RETRY: {
      path: "/appointments/${transactionUuid}/scheduled-messages/${messageUuid}/retry",
      service: "compliance" as Microservice,
    },
    APPOINTMENT_SCHEDULED_MESSAGE_PREVIEW: {
      path: "/appointments/${transactionUuid}/scheduled-messages/${messageUuid}/preview",
      service: "compliance" as Microservice,
    },
    // Bulk variant — one request returns the rendered body for every row on
    // the appointment, used by the Messages tab to avoid N parallel /preview
    // round-trips on mount. Response is an array of the same per-row shape.
    APPOINTMENT_SCHEDULED_MESSAGES_PREVIEWS: {
      path: "/appointments/${transactionUuid}/scheduled-messages/previews",
      service: "compliance" as Microservice,
    },
    // ==========================================
    // CORE MICROSERVICE - Loyalty (points-as-currency MVP)
    // Backend PR2 (config) + PR5 (client account) — NOT deployed yet.
    // ${businessLocationId} is the salon uuid (businessInfo.id), the same
    // value the app already sends as business_location_id elsewhere.
    // ==========================================
    LOYALTY_SETTINGS: {
      path: "/business-locations/${businessLocationId}/loyalty/settings",
      service: "core" as Microservice,
    },
    LOYALTY_SUMMARY: {
      path: "/business-locations/${businessLocationId}/loyalty/summary",
      service: "core" as Microservice,
    },
    LOYALTY_EARN_RULES: {
      path: "/business-locations/${businessLocationId}/loyalty/earn-rules",
      service: "core" as Microservice,
    },
    LOYALTY_EARN_RULE_BY_ID: {
      path: "/business-locations/${businessLocationId}/loyalty/earn-rules/${ruleId}",
      service: "core" as Microservice,
    },
    LOYALTY_REWARDS: {
      path: "/business-locations/${businessLocationId}/loyalty/rewards",
      service: "core" as Microservice,
    },
    LOYALTY_REWARD_BY_ID: {
      path: "/business-locations/${businessLocationId}/loyalty/rewards/${rewardId}",
      service: "core" as Microservice,
    },
    CLIENT_LOYALTY: {
      path: "/clients/${clientId}/loyalty",
      service: "core" as Microservice,
    },
    CLIENT_LOYALTY_ADJUST: {
      path: "/clients/${clientId}/loyalty/adjust",
      service: "core" as Microservice,
    },
    CLIENT_LOYALTY_OPT_IN: {
      path: "/clients/${clientId}/loyalty/opt-in",
      service: "core" as Microservice,
    },
    TRANSACTION_LOYALTY_AVAILABLE_REWARDS: {
      path: "/transactions/${transactionId}/loyalty/available-rewards",
      service: "core" as Microservice,
    },
    TRANSACTION_LOYALTY_REDEEM: {
      path: "/transactions/${transactionId}/loyalty/redeem",
      service: "core" as Microservice,
    },
    TRANSACTION_LOYALTY_PREVIEW_EARN: {
      path: "/transactions/${transactionId}/loyalty/preview-earn",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Client Groups
    // ==========================================
    CLIENT_GROUPS: {
      path: "/business-locations/${businessLocationId}/client-groups",
      service: "core" as Microservice,
    },
    CLIENT_GROUPS_PREVIEW: {
      path: "/business-locations/${businessLocationId}/client-groups/preview",
      service: "core" as Microservice,
    },
    CLIENT_GROUP_BY_ID: {
      path: "/client-groups/${groupId}",
      service: "core" as Microservice,
    },
    CLIENT_GROUP_MEMBERS: {
      path: "/client-groups/${groupId}/members",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Appointment Tags
    // ==========================================
    // GET (any staff) / POST (calendar.canManageAppointmentTags)
    APPOINTMENT_TAGS: {
      path: "/business-locations/${businessLocationId}/appointment-tags",
      service: "core" as Microservice,
    },
    // PUT / DELETE (soft delete); calendar.canManageAppointmentTags
    APPOINTMENT_TAG_BY_ID: {
      path: "/appointment-tags/${tagId}",
      service: "core" as Microservice,
    },
    // PUT {tag_ids}: replace the appointment's tag set (ungated calendar write)
    TRANSACTION_TAGS: {
      path: "/transactions/${transactionId}/tags",
      service: "core" as Microservice,
    },

    // ==========================================
    // CORE MICROSERVICE - Walk-in waitlist
    // ==========================================
    // GET today's waiting rows (ordered) / POST a new walk-in. Gated like
    // appointment create.
    WAITLIST: {
      path: "/business-locations/${businessLocationId}/waitlist",
      service: "core" as Microservice,
    },
    // PUT {uuids}: the full new order → {waitlist}
    WAITLIST_ORDER: {
      path: "/business-locations/${businessLocationId}/waitlist/order",
      service: "core" as Microservice,
    },
    // PUT partial {client_uuid?, salon_service_uuid?, comments?} / DELETE (soft)
    WAITLIST_BY_ID: {
      path: "/waitlist/${waitlistId}",
      service: "core" as Microservice,
    },
    // POST {staff_service_id, start_dt}: books the appointment
    WAITLIST_ASSIGN: {
      path: "/waitlist/${waitlistId}/assign",
      service: "core" as Microservice,
    },

    // ==========================================
    // LEGACY ALIASES (for backward compatibility)
    // These use the same paths as above but with old names
    // ==========================================

    // Time Blocks -> Time Off (legacy aliases)
    TIME_BLOCKS: { path: "/timeoff", service: "core" as Microservice },
    TIME_BLOCKS_BY_SALON: {
      path: "/business-locations/${salonId}/timeoff",
      service: "core" as Microservice,
    },
    DELETE_TIME_BLOCKS: {
      path: "/timeoff/${timeOffId}/${appliesTo}",
      service: "core" as Microservice,
    },

    // Payroll aliases (pointing to rollfi paths)
    PAYROLL_PERIOD: {
      path: "/rollfi/payroll-period",
      service: "payroll" as Microservice,
    },
    PAYROLL_ACTIVE_USERS: {
      path: "/rollfi/active-users",
      service: "payroll" as Microservice,
    },
    PAYROLL_SYNC: {
      path: "/rollfi/sync-payroll",
      service: "payroll" as Microservice,
    },
    PAYROLL_HOURS_RATE: {
      path: "/rollfi/hours-rate",
      service: "payroll" as Microservice,
    },
    PAYROLL_BUSINESS: {
      path: "/rollfi/business",
      service: "payroll" as Microservice,
    },

    // Marketing campaign aliases
    MARKETING_CAMPAIGN_CREATE: {
      path: "/marketing/campaigns",
      service: "marketing" as Microservice,
    },
    UPDATE_CAMPAIGN: {
      path: "/marketing/campaigns",
      service: "marketing" as Microservice,
    },

    // Transaction alias
    TRANSACTION: {
      path: "/transactions",
      service: "core" as Microservice,
    },
  },
} as const;

// Type for endpoint keys
export type EndpointKey = keyof typeof API_CONFIG.ENDPOINTS;

// Helper function to get the base URL for a microservice
export const getMicroserviceUrl = (service: Microservice): string => {
  const url = MICROSERVICE_URLS[service];
  if (!url) {
    throw new Error(
      `Missing base URL for microservice: ${service}. ` +
        `Please set the REACT_APP_${service.toUpperCase()}_API_URL environment variable.`,
    );
  }
  return url;
};

// Type for endpoint config objects (from API_CONFIG.ENDPOINTS.XXX)
interface EndpointConfigObject {
  readonly path: string;
  readonly service: Microservice;
}

/**
 * Helper function to construct API URLs.
 *
 * Supports TWO usage patterns:
 *
 * 1. ENDPOINT KEY (type-safe, recommended):
 *    getApiUrl("STAFF_BY_ID", "", { staffId: "123" })
 *
 * 2. OBJECT FORMAT (from API_CONFIG.ENDPOINTS.XXX):
 *    getApiUrl(API_CONFIG.ENDPOINTS.STAFF_BY_ID, "", { staffId: "123" })
 *
 * The function automatically detects which pattern is being used.
 */
export function getApiUrl(
  endpointKey: EndpointKey,
  queryParams?: string,
  pathParams?: Record<string, string> | null,
): string;
export function getApiUrl(
  endpointConfig: EndpointConfigObject,
  queryParams?: string,
  pathParams?: Record<string, string> | null,
): string;
export function getApiUrl(
  endpointOrConfig: EndpointKey | EndpointConfigObject,
  queryParams: string = "",
  pathParams: Record<string, string> | null = null,
): string {
  // Check if this is an endpoint config object (from API_CONFIG.ENDPOINTS.XXX)
  if (
    typeof endpointOrConfig === "object" &&
    endpointOrConfig !== null &&
    "path" in endpointOrConfig &&
    "service" in endpointOrConfig
  ) {
    const baseUrl = getMicroserviceUrl(endpointOrConfig.service);
    return processUrl(baseUrl, endpointOrConfig.path, queryParams, pathParams);
  }

  // Look up endpoint configuration by key
  const endpoint = API_CONFIG.ENDPOINTS[endpointOrConfig as EndpointKey];
  if (!endpoint) {
    throw new Error(
      `Unknown endpoint key: ${endpointOrConfig}. ` +
        `Please add it to API_CONFIG.ENDPOINTS in src/config/api.ts`,
    );
  }

  const baseUrl = getMicroserviceUrl(endpoint.service);
  return processUrl(baseUrl, endpoint.path, queryParams, pathParams);
}

// Internal helper to process URL with path params
function processUrl(
  baseUrl: string,
  path: string,
  queryParams: string,
  pathParams: Record<string, string> | null,
): string {
  let processedPath = path;

  if (pathParams) {
    const matches = Array.from(path.matchAll(/\${(\w+)}/g));
    for (const match of matches) {
      if (pathParams[match[1]]) {
        processedPath = processedPath.replace(match[0], pathParams[match[1]]);
      }
    }
  }

  return `${baseUrl}${processedPath}${queryParams ? `?${queryParams}` : ""}`;
}

/**
 * Type-safe helper to construct URLs using endpoint keys.
 * Use this for new code to get full TypeScript support.
 *
 * @example
 * getApiUrlTyped("STAFF_BY_ID", "", { staffId: "123" })
 */
export const getApiUrlTyped = (
  endpointKey: EndpointKey,
  queryParams: string = "",
  pathParams: Record<string, string> | null = null,
): string => {
  const endpoint = API_CONFIG.ENDPOINTS[endpointKey];
  const baseUrl = getMicroserviceUrl(endpoint.service);
  return processUrl(baseUrl, endpoint.path, queryParams, pathParams);
};
